import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | null;
const termsContent = `
Términos y Condiciones

Última actualización: Marzo 2026

1. Aceptación de los Términos
Al adquirir el manual "Metabolismo en Punto de Equilibrio (40+)", aceptas estos términos y condiciones en su totalidad. Si no estás de acuerdo con alguno de estos términos, te pedimos que no realices la compra.

2. Descripción del Producto
Este es un producto digital educativo en formato descargable. El contenido tiene fines informativos y educativos, y no sustituye el consejo médico profesional. Siempre consulta con un profesional de la salud antes de realizar cambios en tu rutina de salud.

3. Licencia de Uso
Al completar tu compra, recibes una licencia personal e intransferible para usar el contenido. No está permitido copiar, distribuir, revender ni compartir el material con terceros.

4. Política de Reembolso
Debido a la naturaleza digital del producto, todas las ventas son finales una vez que se ha entregado el acceso al contenido. Si experimentas problemas técnicos para acceder al material, contáctanos y te ayudaremos a resolverlo.

5. Propiedad Intelectual
Todo el contenido del manual, incluyendo textos, gráficos, diseños y metodologías, está protegido por derechos de autor. Queda prohibida su reproducción total o parcial sin autorización expresa.

6. Limitación de Responsabilidad
Los resultados pueden variar de persona a persona. No garantizamos resultados específicos. El contenido se proporciona "tal cual" y no nos hacemos responsables de decisiones tomadas basándose en la información proporcionada.

7. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones serán efectivas desde su publicación.
`.trim();
const privacyContent = `
Política de Privacidad

Última actualización: Marzo 2026

1. Información que Recopilamos
Al realizar tu compra, recopilamos únicamente la información necesaria para procesar la transacción: nombre, dirección de correo electrónico y datos de pago. Los datos de pago son procesados de forma segura por nuestra plataforma de pagos (Hotmart) y no almacenamos información de tarjetas de crédito.

2. Uso de la Información
Tu información personal se utiliza exclusivamente para:
• Procesar y entregar tu compra.
• Enviarte el acceso al producto digital.
• Comunicarnos contigo en caso de ser necesario respecto a tu compra.

3. Protección de Datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. Compartir Información
No vendemos, alquilamos ni compartimos tu información personal con terceros, excepto cuando sea necesario para procesar tu compra a través de nuestra plataforma de pagos.

5. Cookies
Nuestro sitio puede utilizar cookies para mejorar la experiencia de navegación. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar la funcionalidad del sitio.

6. Tus Derechos
Tienes derecho a:
• Acceder a tus datos personales.
• Solicitar la corrección de datos inexactos.
• Solicitar la eliminación de tus datos.
• Retirar tu consentimiento en cualquier momento.

7. Cambios en esta Política
Nos reservamos el derecho de actualizar esta política de privacidad. Cualquier cambio será publicado en esta página.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 border-t border-white/5" data-id="element-38">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8" data-id="element-39">
          <button onClick={() => setActiveModal('terms')} className="text-sm text-gray-500 hover:text-emerald-400 transition-colors" data-id="element-40">
            Términos y Condiciones
          </button>
          <span className="hidden sm:inline text-gray-700" data-id="element-41">|</span>
          <button onClick={() => setActiveModal('privacy')} className="text-sm text-gray-500 hover:text-emerald-400 transition-colors" data-id="element-42">
            Política de Privacidad
          </button>
        </div>
        <p className="text-center text-xs text-gray-600 mt-4" data-id="element-43">
          © {new Date().getFullYear()} Metabolismo en Punto de Equilibrio
          (40+). Todos los derechos reservados.
        </p>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-44">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-45">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} onClick={e => e.stopPropagation()} className="relative bg-[#162019] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" data-id="element-46">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-47">
                <h3 className="text-lg font-semibold text-white" data-id="element-48">
                  {activeModal === 'terms' ? 'Términos y Condiciones' : 'Política de Privacidad'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors" data-id="element-49">
                  <XIcon className="w-5 h-5" data-id="element-50" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-51">
                <div className="text-gray-300 text-sm leading-relaxed whitespace-pre-line" data-id="element-52">
                  {activeModal === 'terms' ? termsContent : privacyContent}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}